import type { ReactNode } from "react";

import { Link } from "@/lib/nav";
import { cn } from "@/lib/utils";

export interface GroupedListItem {
    label: ReactNode;
    value: ReactNode;
    /** Valor em destaque (`title-2`), ex.: prêmio anual. */
    emphasis?: boolean;
    /** Texto longo (motivo, observação): rótulo em cima, valor embaixo, alinhado à esquerda. */
    stacked?: boolean;
}

interface GroupedListProps {
    title?: ReactNode;
    /** Ícone da seção (`SectionIcon`): o título passa a ser o nome do bloco, em `label`. */
    icon?: ReactNode;
    /** Ação à direita do título (botão `sm` ghost/link). */
    action?: ReactNode;
    items: GroupedListItem[];
    /** Linha de ações ao pé da lista. */
    footer?: ReactNode;
    className?: string;
}

/** Cabeçalho de um bloco em superfície: ícone da seção, título e ação. Compartilhado por `GroupedList` e `Panel`. */
export function PanelHeader({ title, icon, action }: { title: ReactNode; icon?: ReactNode; action?: ReactNode }) {
    if (!icon) return <h3 className="px-4.5 pb-2 pt-3.5 text-subhead font-semibold text-label-secondary">{title}</h3>;
    return (
        <div className="flex min-h-14 flex-wrap items-center gap-2.5 pb-1.5 pl-4.5 pr-2.5 pt-3">
            {icon}
            <h3 className="flex-1 text-body font-semibold text-label">{title}</h3>
            {action}
        </div>
    );
}

/** Bloco em superfície com cabeçalho e conteúdo livre (lista de linhas que não são rótulo → valor). */
export function Panel({ title, icon, action, children, className }: { title: ReactNode; icon?: ReactNode; action?: ReactNode; children: ReactNode; className?: string }) {
    return (
        <section className={cn("flex flex-col overflow-hidden rounded-lg bg-surface shadow-card", className)}>
            <PanelHeader title={title} icon={icon} action={action} />
            {children}
        </section>
    );
}

const LINHA = "flex min-h-[52px] items-center gap-3 border-t border-separator-soft px-4.5 py-2 first:border-t-0";
const LINHA_CLICAVEL =
    "w-full text-left transition-colors duration-150 hover:bg-row-hover focus-visible:outline focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-green motion-reduce:transition-none";

interface PanelRowProps {
    children: ReactNode;
    className?: string;
    /** Linha que leva a outra tela (link focável). */
    href?: string;
    /** Linha que age na própria tela (botão). */
    onClick?: () => void;
}

/** Linha de um `Panel` (separador entre linhas, nunca acima da primeira); com `href` ou `onClick` a linha inteira é clicável. */
export function PanelRow({ children, className, href, onClick }: PanelRowProps) {
    if (href) {
        return (
            <Link href={href} className={cn(LINHA, LINHA_CLICAVEL, className)}>
                {children}
            </Link>
        );
    }
    if (onClick) {
        return (
            <button type="button" onClick={onClick} className={cn(LINHA, LINHA_CLICAVEL, className)}>
                {children}
            </button>
        );
    }
    return <div className={cn(LINHA, className)}>{children}</div>;
}

/** Pares rótulo → valor de um registro (dados do cliente, do sistema, da cobrança), no estilo de ajustes da Apple. */
export function GroupedList({ title, icon, action, items, footer, className }: GroupedListProps) {
    return (
        <section className={cn("flex flex-col overflow-hidden rounded-lg bg-surface shadow-card", className)}>
            {title && <PanelHeader title={title} icon={icon} action={action} />}
            <dl>
                {items.map((item, i) => (
                    <div
                        key={i}
                        className={cn(
                            "flex min-h-11 gap-3 px-4.5 py-[11px]",
                            item.stacked ? "flex-col gap-0.5" : "items-center justify-between",
                            ((title && !icon) || i > 0) && "border-t border-separator-soft",
                        )}
                    >
                        <dt className="whitespace-nowrap text-label-secondary">{item.label}</dt>
                        <dd
                            className={cn(
                                "min-w-0",
                                item.stacked ? "whitespace-pre-line break-words" : "truncate text-right",
                                item.emphasis ? "text-title-2" : "font-medium",
                            )}
                        >
                            {item.value}
                        </dd>
                    </div>
                ))}
            </dl>
            {footer && <div className="mt-auto flex flex-wrap gap-2 border-t border-separator-soft px-4.5 py-3">{footer}</div>}
        </section>
    );
}
